/**
 * @ourbranch/env-helpers — ESM entry point
 *
 * Mirrors src/index.js (CommonJS) for ESM consumers like Vite/Rollup-built
 * frontends and TypeScript lambdas. Both files MUST stay in sync — keep
 * the logic identical when changing either one.
 *
 * Routing is handled by the `exports` field in package.json:
 *   - `import { isProductionEnv } from '@ourbranch/env-helpers'` → this file
 *   - `require('@ourbranch/env-helpers')` → src/index.js
 */

export const PROTECTED_ENVS = ['march2019', 'preprod'];

export const EPHEMERAL_ENVS = [
  'demo',
  'staging',
  'staging1',
  'staging2',
  'staging3',
  'staging4',
  'staging5',
  'staging6',
  'staging7',
  'staging8',
  'staging9'
];

/**
 * Returns true if the given environment name is a production environment.
 * Matches both 'production' (Branch) and '{carrier}-production' (e.g., forreston-production).
 */
export function isProductionEnv(env) {
  return env?.endsWith('production') ?? false;
}

/**
 * Returns true if the given environment name is a protected environment.
 * Protected environments include all production environments plus march2019 and preprod.
 */
export function isProtectedEnv(env) {
  return isProductionEnv(env) || PROTECTED_ENVS.includes(env);
}

/**
 * Returns true if the given environment name is an ephemeral environment.
 * Ephemeral environments are safe to write short-lived / disposable data to
 * (demo and the 10 staging CI slots).
 */
export function isEphemeralEnv(env) {
  return EPHEMERAL_ENVS.includes(env);
}

/**
 * Builds a fully-qualified staff app URL for the current brand and environment.
 * In production, composes the host from a subdomain (e.g. 'staff', 'team') and
 * domain (e.g. 'ourbranch.com', 'forreston.embermutuals.com').
 *
 * Resolution order for the production host (issue #31519): explicit per-brand
 * `subdomain`/`domain` options (sourced from brand config) take precedence over
 * the deploy-time STAFF_SUBDOMAIN/BRAND_DOMAIN env vars, which take precedence
 * over the Branch defaults. Threading the brand explicitly means a single
 * deployment that knows its brand (e.g. dynamo-be, which sets BRAND_NAME but not
 * STAFF_SUBDOMAIN) still links to the carrier-branded staff app rather than
 * staff.ourbranch.com. In non-production, falls back to the shared ourbranch.dev
 * staging pattern (brand overrides do not apply).
 *
 * The second argument accepts either an options object `{ env, subdomain, domain }`
 * or, for backwards compatibility, a bare `env` string.
 */
export function buildStaffUrl(path, options) {
  const {
    env = process.env.STACKERY_ENVIRONMENT_NAME,
    subdomain,
    domain
  } = typeof options === 'string' ? { env: options } : options || {};
  const normalized = path.replace(/^\/+/, '');
  if (isProductionEnv(env)) {
    const resolvedSubdomain = subdomain || process.env.STAFF_SUBDOMAIN || 'staff';
    const resolvedDomain = domain || process.env.BRAND_DOMAIN || 'ourbranch.com';
    return `https://${resolvedSubdomain}.${resolvedDomain}/${normalized}`;
  }
  return `https://staff.${env}.ourbranch.dev/${normalized}`;
}
